// API連携

/**
 * 取得するデータの種類
 * recommend - おすすめの商品
 * facorite - お気に入り
 * checked - 最近チェックした商品
 * @typedef {'recommend'| 'favorite' | 'checked' | ''} LinkageType
 */

/**
 * 商品の販売ステータス
 * now - 販売中
 * before - 販売開始前
 * after - 販売終了
 * sold - 完売
 * @typedef {"now" | "before" | "after" | "sold"} ProductDataStatus
 */

/**
 * @typedef ProductAmenitySelection
 * @property {string} id - 商品特典ID
 * @property {string} text - 商品特典のタイトル
 */

/**
 * @typedef ProductAmenity
 * @property {string} title - 商品特典のタイトル
 * @property {string} type - 特典の種別
 * @property {ProductAmenitySelection[]} selection
 */

/**
 * 商品の詳細情報
 * @typedef Product
 * @property {string} id- 商品ID
 * @property {string} category - 商品カテゴリ
 * @property {string} price - 金額
 * @property {ProductAmenity[]} amenities
 */

/**
 * 商品情報
 * @typedef ProductData
 * @property {string} category - 商品カテゴリ
 * @property {string} release - リリース日
 * @property {ProductDataStatus} status - 販売ステータス
 * @property {string} title - 商品タイトル
 * @property {string} thumbnail - サムネイルURL
 * @property {string} url - 商品URL
 * @property {string} limited - 限定商品かどうか
 * @property {Product[]} items - 商品の詳細情報リスト
 */

/**
 * APIの返り値
 * @typedef ProductResponse
 * @property {ProductData[]} [products] - 商品情報
 */

// import global variables.
import {getStorageItem} from '../utils/global';
import createProductList from './createProductList';

const baseUrl = '/api/'; // リリース時は`/api/`を指定

/**
 * fetchData - APIからJSONを取得
 * @param  {string} url - APIへリクエストするパラメータ付きURL
 * @return {Promise<ProductResponse>}} - APIから返却されたJSON（エラー時は空のオブジェクト）
 */
const fetchData = async (url) => {
    try {
        const response = await fetch(url, {
            mode: 'cors', // リリース時は同ドメイン・BASIC認証無しになるので不要
            credentials: 'include' // リリース時は同ドメイン・BASIC認証無しになるので不要
        });
        const json = await response.json();

        return json;
    } catch (e) {
        return {};
    }
};

/**
 * getDataWithin30Days - 閲覧履歴に保存されたアイテムの内、直近30日以内のidのみを「,」で連結し、返却
 * @param  {Object} dataArray 閲覧履歴のデータが格納されたオブジェクト
 * @return {String}           直近30日以内に閲覧した商品のid名が「,」で連結された文字列
 */
const getDataWithin30Days = (dataArray) => {
    const date = new Date();
    const before30DaysDate = date.setDate(date.getDate() - 31); // 31日前を設定
    const resultData = [];

    // 各アイテムが30日以内に閲覧されたものか検証
    for (const data of dataArray) {
        if (data.time < before30DaysDate) {
            continue;
        }

        resultData.push(data.id);
    }

    return resultData.join(',');
};

/**
 * createPostParam - APIへ送信するパラメータを生成
 * @param  {LinkageType} type - 取得するデータの種類
 * @return {string} - APIへ送信するパラメータ
 */
const createPostParam = (type) => {
    const storageValue = getStorageItem(type === 'checked' ? 'history' : 'favorite');
    const storageData = (storageValue && JSON.parse(String(storageValue))) || {};
    const storageDataKeys = Object.keys(storageData);
    let paramText = '';

    // ローカルストレージに格納されている情報が空だった場合、空文字を返却
    if (!storageDataKeys.length) {
        return paramText;
    }

    // 取得するデータの種類が「おすすめの商品」または「お気に入り」の場合
    if (type === 'recommend' || type === 'favorite') {
        const productParamItemIdNames = [];
        const workParamItemIdNames = [];

        // 作品、声優・アーティストのidを一つにまとめる必要があるための処理
        for (const key of storageDataKeys) {
            if (key !== 'product') {
                workParamItemIdNames.push(storageData[key]);

                continue;
            }

            productParamItemIdNames.push(storageData[key]);
        }

        // ?type=(recommend|favorite)&product=商品ID,商品ID...&work=作品ID,作品ID,声優ID,声優ID...
        paramText += `?type=${type}&product=${productParamItemIdNames.join(',')}&work=${workParamItemIdNames.join(',')}`;
    } else if (type === 'checked') {
        // ?history=商品ID,商品ID,商品ID...
        paramText = `?history=${getDataWithin30Days(storageData)}`;
    }

    return paramText;
};

/**
 * デフォルト実行処理
 * @param  {NodeListOf<HTMLElement>} rootElements - ルートとなる要素群
 * @return {Void}
 */
export default (rootElements) => {
    // ルート要素が無ければ何もしない
    if (!rootElements.length) {
        return;
    }

    // ルート要素にIntersectionObserverを実装
    for (const element of rootElements) {
        /** @type {LinkageType} */
        const linkageType = (element.dataset.linkageType || '');

        // [data-linkage-type]が指定されていない場合は実装をスルー
        if (!linkageType) {
            continue;
        }

        const observer = new IntersectionObserver((entries) => {
            if (!entries) {
                return;
            }

            // データのフェッチは遅延させる
            for (const entry of entries) {
                const {target} = entry;

                if (!entry.isIntersecting) {
                    continue;
                }

                // APIとの通信
                fetchData(`${baseUrl}${createPostParam(linkageType)}`).then((response) => {
                    createProductList(element, linkageType, response);
                });

                observer.unobserve(target);
            }
        }, {
            rootMargin: '100px 0px'
        });

        // 監視を開始
        observer.observe(element);
    }
};
